import APITable from '@/components/UI/Table/Reusable/APITable';
import { RouteService } from '@/support/RouteService';
import { matchPath, useNavigate, useLocation } from 'react-router-dom';
import { Study, StudyApiId } from '@/model/Study.model';
import UserAvatar from '@/components/UI/UserAvatar';
import { DateTimeRenderer } from '@/components/UI/DateRenderers/DateRenderers';
import { modalAction } from '@/utils/modal';
import { useDispatch, useSelector } from 'react-redux';
import { web as webRoute } from '@/support/route';
import { DosageV1, DoseApiId } from '@/model/Dose.model';
import { useApiHook } from '@/support/Hooks/api/useApiHook';
import { _notNil } from '@/littledash';
import Link from '@/components/UI/Link';
import { FC, useEffect, useMemo, useState } from 'react';
import { formatNumber, successToast } from '@/helpers';
import * as Auth from '@/support/auth';
import { LegacyApiService } from '@/support/LegacyApiService.ts';
import { classificationTypes } from '@/components/Studies/Treatments/Treatment.utils.tsx';
import { ClassificationType } from '@/model/Treatment.model.ts';
import SelectDropDown from '@/components/UI/SelectDropDown';
import ActionList from '@/components/UI/SelectDropDown/Menus/ActionList.tsx';
import { createSelector } from '@reduxjs/toolkit';
import { State } from '@/model/State.model.ts';
import Button from '@/components/UI/Button';
import { TanStackColumnDef } from '@/components/UI/Table/TableComponent.model';

export type DosageValues = {
  display_value: string;
  display_unit: string;
};

export type RowData = {
  dose_volume: DosageValues;
  volume: DosageValues;
  dose: DosageValues;
  stock: DosageValues;
  animal: { id: number; name: string };
  cage: { name: string };
  name: string;
  treatment: { treatment_classification: ClassificationType };
  notes: string;
  user: any;
  created_at: string;
  dosed_at: string;
};

export const columns = (studyId: string): Array<TanStackColumnDef<RowData>> => [
  {
    id: 'animal',
    header: 'Animal',
    accessorFn: (row: RowData) => row.animal?.name,
    isVisible: true,
    enableSorting: true,
    cell: ({ row: { original } }: { row: { original: RowData } }) => (
      <Link
        className="link"
        to={webRoute('animals.show', {
          id: studyId,
          subject_id: original.animal?.id,
        })}
      >
        {original.animal?.name}
      </Link>
    ),
  },
  {
    id: 'cage',
    header: 'Cage',
    isVisible: true,
    enableSorting: true,
    accessorFn: (row: RowData) => row.cage?.name,
  },
  {
    id: 'name',
    header: 'Treatment',
    accessorKey: 'name',
    isVisible: true,
    enableSorting: true,
  },
  {
    id: 'treatment_classification',
    header: 'Classification',
    isVisible: true,
    enableSorting: false,
    cell: ({ row: { original } }: { row: { original: RowData } }) =>
      classificationTypes[original.treatment?.treatment_classification as ClassificationType]?.title,
  },
  {
    id: 'dose',
    header: 'Dose',
    isVisible: true,
    enableSorting: false,
    cell: ({ row }: { row: { original: RowData } }) =>
      formatDosingValue(row.original.dose?.display_value, row.original.dose?.display_unit),
  },
  {
    id: 'dose_volume',
    header: 'Dose Volume',
    isVisible: true,
    enableSorting: false,
    cell: ({ row }: { row: { original: RowData } }) =>
      formatDosingValue(row.original.dose_volume?.display_value, row.original.dose_volume?.display_unit),
  },
  {
    id: 'stock',
    header: 'Stock conc',
    isVisible: true,
    enableSorting: false,
    cell: ({ row }: { row: { original: RowData } }) =>
      formatDosingValue(row.original.stock?.display_value, row.original.stock?.display_unit),
  },
  {
    id: 'volume',
    header: 'Volume',
    isVisible: true,
    enableSorting: false,
    cell: ({ row }: { row: { original: RowData } }) =>
      formatDosingValue(row.original.volume?.display_value, row.original.volume?.display_unit),
  },
  {
    id: 'notes',
    header: 'Comments',
    accessorKey: 'notes',
    isVisible: true,
    enableSorting: false,
  },
  {
    id: 'user',
    header: 'User',
    accessorKey: 'user',
    isVisible: true,
    enableSorting: true,
    cell: ({ row: { original } }: { row: { original: RowData } }) => (
      <UserAvatar user={original?.user} tooltip={original?.user?.name ?? original?.user?.email} />
    ),
  },
  {
    id: 'created_at',
    header: 'Created',
    accessorKey: 'created_at',
    isVisible: true,
    enableSorting: true,
    cell: ({ row: { original } }: { row: { original: RowData } }) => <DateTimeRenderer value={original.created_at} />,
  },
  {
    id: 'dosed_at',
    header: 'Dosed',
    accessorKey: 'dosed_at',
    isVisible: true,
    enableSorting: true,
    cell: ({ row: { original } }: { row: { original: RowData } }) => <DateTimeRenderer value={original.dosed_at} />,
  },
];

const formatDosingValue = (value: string | number | null, unit: string | null) => {
  if (_notNil(value)) {
    return `${formatNumber(value, true, 3)} ${unit}`;
  } else {
    return '-';
  }
};

export const ListDosing: FC = () => {
  const { pathname } = useLocation();
  const path = matchPath('/studies/:studyId/dosing/*', pathname);
  const studyId = path?.params?.studyId as StudyApiId;
  const dispatch = useDispatch();
  const { openModal, closeModal } = modalAction(dispatch);
  const [study, setStudy] = useState<Study>();
  const navigate = useNavigate();

  const selectDiseaseInduction = createSelector(
    (state: State) => state.team?.features?.disease_induction ?? false,
    (disease_induction) => ({ disease_induction })
  );
  const { disease_induction } = useSelector(selectDiseaseInduction);

  const recordDose = (classification: ClassificationType) => {
    navigate(
      RouteService.web({
        route: 'studies.dosing.create',
        path: { id: studyId, classification: classification as string },
      }).route
    );
  };

  const actions = [
    {
      name: 'Record Treatment',
      action: () => recordDose('treatment'),
    },
    {
      name: 'Record Disease',
      action: () => recordDose('disease_induction'),
    },
    {
      name: 'Record Pre-treatment',
      action: () => recordDose('pre_treatment'),
    },
  ];

  useEffect(() => {
    LegacyApiService.call({
      method: 'GET',
      apiRoute: 'studies.show.p',
      query: { include: 'study_groups,api_id,users' },
      path: { id: studyId },
    }).then((data) => {
      setStudy((data.body as { data: Study })?.data ?? undefined);
    });
  }, []);

  // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
  const isWriteUser = study ? Auth.isWriteUserForStudy(study) : false;

  const memoizedColumns = useMemo(() => columns(studyId), [studyId]);

  const { invoke } = useApiHook({
    endpoint: 'DELETE /api/v1/studies/{studyId}/dosages',
    invokeOnInit: false,
  });

  const handleDelete = async (
    refetchTableData: () => Promise<void>,
    doses: Array<DosageV1>,
    reasonForDelete?: string
  ) => {
    if (_notNil(study?.api_id)) {
      try {
        await invoke({
          body: {
            dosages: doses.reduce((acc: Array<DoseApiId>, dose) => {
              if (_notNil(dose?.api_id)) {
                acc.push(dose.api_id);
              }
              return acc;
            }, []),
            reason_for_change: reasonForDelete,
          },
          path: { studyId: study!.api_id },
        });
        closeModal();
        successToast('Successfully deleted!');
        await refetchTableData();
      } catch (cause) {
        dispatch({ type: 'SET_ERROR', data: cause });
      }
    }
  };

  const bulkActions = ({
    useTableProps: {
      selectedFlatRows,
      apiTable: { refetchTableData },
    },
  }: any) => {
    if (!isWriteUser) {
      return [];
    }
    const doses = selectedFlatRows.map((row: any) => row.original);

    return [
      {
        name: 'Delete',
        action: () =>
          openModal('CONFIRM_DELETE_DOSES', {
            subject: [doses],
            closeModal,
            onClick: (reasonForDelete?: string) => handleDelete(refetchTableData, doses, reasonForDelete),
          }),
        className: 'red',
      },
    ];
  };
  return (
    <>
      <div className="ph4 pt3">
        {_notNil(study?.api_id) && (
          <APITable
            AsideComponent={
              <>
                {isWriteUser &&
                  (disease_induction ? (
                    <SelectDropDown
                      title="Record Dose"
                      alignMenu="right"
                      className="pr4"
                      testId="create-new-dose-dropdown"
                    >
                      <ActionList actions={actions} />
                    </SelectDropDown>
                  ) : (
                    <Button url={webRoute('studies.dosing.create', { id: studyId, classification: 'treatment' })}>
                      Record dose
                    </Button>
                  ))}
              </>
            }
            apiInfo={{
              type: 'internalApi',
              route: RouteService.api({
                endpoint: 'GET /api/v1/studies/{studyId}/dosages',
                path: { studyId: study!.api_id },
              }).url,
            }}
            includeParam="users,animal,api_id"
            columns={memoizedColumns}
            bulkActions={bulkActions}
            reduxTableName={'doses'}
            showViewSelector
            showMetricChangeFrom={true}
            pageSizes={[50, 100, 200, 500, 1000]}
            defaultPageSize={1000}
            testIdPrefix="doses"
            hideSearch={false}
            searchPlaceholderText="Search by Name"
          />
        )}
      </div>
    </>
  );
};
